import type { AutocompleteRenderInputParams } from "@mui/material";
import type { ArtworkNatureOption } from "artworkNatureList";
import { artworkNatureList } from "artworkNatureList";
import Plus from "assets/img/svg/plus";
import CreateAnalyticalCode from "components/NewTitle/CreateAnalyticalCode";
import CustomFieldsCollapse from "components/shared/CustomFieldsCollapse";
import AutocompleteFormComponent from "components/shared/FormComponent/AutocompleteFormComponent";
import ImageFormComponent from "components/shared/FormComponent/ImageFormComponent";
import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import InfoTooltip from "components/shared/InfoTooltip";
import ModalBase from "components/shared/ModalBase";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { PortalType } from "contexts/portal.context";
import { usePermissions } from "hooks/usePermissions";
import type { HTMLAttributes } from "react";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ICustomField } from "store/customFieldsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { fetchAnalyticalCodes } from "store/titleStore";

import type { TPublicationFormData } from "..";

interface INewPublicationStep {
    stepTitle: string;
    stepSubtitle: string;
    mode: "new" | "edit-page";
    indexStep: number;
    useFormData: TPublicationFormData;
    isPreferredIdentifierConcat: boolean;
    companyId: number | undefined;
    customFields: ICustomField[];
}

const NewPublicationStep = ({
    stepTitle,
    stepSubtitle,
    indexStep,
    mode,
    useFormData,
    isPreferredIdentifierConcat,
    companyId,
    customFields,
}: INewPublicationStep) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const portalType = useContext(PortalType);
    const { control, errors, watch, setValue } = useFormData;

    const { sendPictureStatus, codes, codesStatus } = useAppSelector((state) => state.titles);

    const { hasPermissions } = usePermissions();

    // Filter fields that should not appear on creation :
    const customFieldsOnCreation = customFields.filter((field) => field.showOnCreation);

    // Filter value for the analysis code autocomplete:
    const [filterValue, setFilterValue] = useState("");
    const [modalType, setModalType] = useState("");

    const renderArtworkNatureOption = (props: HTMLAttributes<HTMLLIElement>, option: ArtworkNatureOption) => (
        <li {...props} className="artwork-nature-list">
            <span>{option.code}</span>
            <div className="separator"></div>
            <span>{option.description}</span>
        </li>
    );

    /**
     * Array with only names of analytical codes.
     */
    const analyticalCodeNames: string[] = useMemo(() => {
        return codes.map((code: any) => code.name);
    }, [codes]);

    /**
     * Array with analytical codes filtered within autocomplete.
     */
    const filteredAnalyticalCodes = useMemo(() => {
        return (
            codes &&
            analyticalCodeNames.filter((name: string) => name.toLowerCase().includes(filterValue.toLowerCase()))
        );
    }, [codes, filterValue]);

    const renderInputAnalyticalCode = (params: AutocompleteRenderInputParams) => (
        <StyledTextField
            {...params}
            onChange={(e) => setFilterValue(e.target.value)}
            helperText={errors.analysisCode?.message ?? ""}
        />
    );

    const renderAnalyticalCodeOption = (
        props: HTMLAttributes<HTMLLIElement>,
        option: string & { addElement?: string },
    ) => {
        return option.addElement ? (
            <li {...props} key={`option-add-element`} className="autocomplete-new-element">
                <div className="content">
                    <Plus />
                    {option.addElement}
                </div>
            </li>
        ) : (
            <li {...props} key={`option-${option}`}>
                <span>{option}</span>
            </li>
        );
    };

    const selectedAnalyticalCode = watch!("analysisCode");

    // Opens modal for creating a new analytical code.
    useEffect(() => {
        if ((selectedAnalyticalCode as any)?.id == -1) {
            setModalType("analyticalCode");
            setValue!("analysisCode", "");
        }
    }, [selectedAnalyticalCode]);

    const [newAnalyticalCode, setNewCodeName] = useState("");

    // When new analytical code created, sets value of field.
    useEffect(() => {
        if (newAnalyticalCode === "") return;
        setValue!("analysisCode", analyticalCodeNames?.find((name: string) => name === newAnalyticalCode) ?? "");
    }, [newAnalyticalCode, analyticalCodeNames]);

    // Fetches analytical codes on form load:
    useEffect(() => {
        if (codesStatus === "idle") {
            dispatch(fetchAnalyticalCodes());
        }
    }, [codesStatus]);

    return (
        <>
            <h3 className="form-section-title first">
                {" "}
                {indexStep + 1}. {stepTitle}
                {mode === "edit-page" && (
                    <InfoTooltip
                        text={t("TransferTerms.Helpdesk.AccessDoc")}
                        link="https://presse-helpdesk.crealo.app/fr/article/modifier-une-parution-1qj5c2q/"
                        iconType="question"
                    />
                )}
            </h3>
            <span className="form-section-description">{stepSubtitle}</span>
            <div className="form-fields">
                <TextFormComponent
                    label={t(`NewPublication.PublicationName_${portalType}`)}
                    placeholder={""}
                    control={control}
                    controlName="publicationName"
                    error={errors.publicationName}
                    required
                    customRequiredMessage={t(`NewPublication.RequiredName_${portalType}`)}
                    disabled={!hasPermissions(["publication:new"])}
                />
                <ImageFormComponent
                    label={t(`NewPublication.Avatar_${portalType}`)}
                    avatar={watch!("avatarUrl")}
                    setAvatar={(name: string, value: any) => setValue!("avatarUrl", value, { shouldDirty: true })}
                    sendPictureStatus={sendPictureStatus}
                    control={control}
                    controlName="avatarUrl"
                    type="publication"
                    disabled={!hasPermissions(["publication:new"])}
                />
                <TextFormComponent
                    label={isPreferredIdentifierConcat ? t("NewPublication.ISSNCode") : t("NewPublication.UniqueCode")}
                    placeholder={t("NewPublication.ISSNPlaceholder")}
                    control={control}
                    required
                    controlName="issnCode"
                    error={errors.issnCode}
                    disabled={!hasPermissions(["publication:new"])}
                />
                {isPreferredIdentifierConcat && (
                    <TextFormComponent
                        label={t("NewPublication.ParutionCode")}
                        placeholder={t("NewPublication.ParutionPlaceholder")}
                        control={control}
                        required={isPreferredIdentifierConcat}
                        controlName="parutionCode"
                        error={errors.parutionCode}
                        disabled={!hasPermissions(["publication:new"])}
                    />
                )}
                <AutocompleteFormComponent
                    label={t("ImportData.TitleAnalysisCode")}
                    options={filteredAnalyticalCodes || []}
                    renderInput={renderInputAnalyticalCode}
                    renderOptionText={(option: any) => `${option}`}
                    renderOption={renderAnalyticalCodeOption}
                    control={control}
                    controlName="analysisCode"
                    error={errors.analysisCode}
                    addElement={t("AnalyticalCode.AddAnalyticalCode")}
                    isOptionEqualToValue={(option: string, value: string) => option === value || value === ""}
                    disabled={!hasPermissions(["publication:new"])}
                />
                <AutocompleteFormComponent
                    label={t("NewPublication.ArtworkNature")}
                    options={artworkNatureList}
                    renderOptionText={(option: ArtworkNatureOption) => `${option.code} | ${option.description}`}
                    renderOption={renderArtworkNatureOption}
                    defaultFilterOptions
                    control={control}
                    controlName="artworkNature"
                    error={errors.artworkNature}
                    required
                    customRequiredMessage={t("NewPublication.RequiredArtworkNature")}
                    isOptionEqualToValue={(option: ArtworkNatureOption, value: ArtworkNatureOption) =>
                        option.code === value.code
                    }
                    disabled={!hasPermissions(["publication:new"])}
                />
                {/** COLLAPSE FOR THE CUSTOM FIELDS */}
                {customFields.length > 0 && (
                    <CustomFieldsCollapse
                        sectionFields={mode === "new" ? customFieldsOnCreation : customFields}
                        errors={errors}
                        mode={mode}
                    />
                )}
            </div>
            <ModalBase
                open={modalType === "analyticalCode"}
                onClose={() => setModalType("")}
                modalTitle={t("AnalyticalCode.AddAnalyticalCode")}
            >
                <CreateAnalyticalCode
                    close={() => setModalType("")}
                    onCreate={(name: string) => {
                        setNewCodeName(name);
                    }}
                    companyId={companyId}
                />
            </ModalBase>
        </>
    );
};

export default NewPublicationStep;
